import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import camelcaseKeys from 'camelcase-keys';
import queryString from 'query-string';
import { Box, CircularProgress } from '@mui/material';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { VOICE_STATUS } from '@src/constants/voiceCloning';
import CategoryPick from './CategoryPick';
import AddInformation from './AddInformation';
import Notes from './Notes';
import Recording from './Recording';
import CheckInformation from './CheckInformation';
import SendRequest from './SendRequest';
import NoteRecordingAgain from './NoteRecordingAgain';

const VOICE_CLONING_STEP = {
  SELECT_CATEGORY: 1,
  ADD_INFOMATION: 2,
  NOTES: 3,
  RECORDING: 4,
  CHECK_INFORMATION: 5,
  SEND_REQUEST: 6,
};

const VoiceCloningCreate = () => {
  const history = useHistory();
  const location = useLocation();
  const { voiceId } = useParams();
  const dispatch = useDispatch();
  const { audios } = useSelector((state) => state.audioVoiceCloning);
  const { voice: voiceRecording } = useSelector(
    (state) => state.voiceVoiceCloning,
  );
  const [hasRecording, setHasRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audiosCurrent, setAudiosCurrent] = useState([]);
  const [sentences, setSentences] = useState([]);
  const [step, setStep] = useState(null);
  const [latestEmailUsed, setLatestEmailUsed] = useState('');

  if (!isMobile) history.push(route.VOICE_CLONING_VOICES);

  const getCategoryInfo = async (categoryId) => {
    const response = await apis.voiceCloning.getVoiceCloningCategories();
    if (!response?.status) return null;

    const categoriesArray = response?.result?.categories || [];
    const category = categoriesArray.find((item) => item.id === categoryId);
    return category;
  };

  const fetchVoice = async (id) => {
    setIsLoading(true);
    const response = await apis.voiceCloning.getVoiceCloningVoice(id);

    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getVoiceFailed',
        }),
      );
      setIsLoading(false);
      return;
    }
    const recording = response?.result?.voice;
    if (
      !recording ||
      ![
        VOICE_STATUS.RECORDING,
        VOICE_STATUS.REJECTED,
        VOICE_STATUS.FAILED,
      ].includes(recording?.status)
    ) {
      history.push(route.VOICE_CLONING_VOICES);
      return;
    }
    const category = await getCategoryInfo(recording?.categoryId);
    if (!category) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getVoiceFailed',
        }),
      );
      setIsLoading(false);
      return;
    }
    const voiceRecordingInfo = {
      ...recording,
      category,
    };
    dispatch(actions.voiceVoiceCloning.addVoice(voiceRecordingInfo));
    setHasRecording(!!recording);
    setIsLoading(false);
  };

  const fetchAudios = async (voiceRecordId) => {
    setIsLoading(true);
    const response = await apis.voiceCloning.getVoiceCloningAudios(
      voiceRecordId || voiceId,
    );

    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getAudiosFailed',
        }),
      );
      setIsLoading(false);
      return;
    }
    const audiosArray = response?.result?.audios || [];

    // const audiosShowed = audiosArray.filter((audio) => {
    //   if (voiceRecording?.status === VOICE_STATUS.REJECTED)
    //     return audio.status === NEW_AUDIO_STATUS.FAILED;
    //   return true;
    // });

    dispatch(actions.audioVoiceCloning.updateAudios(audiosArray));
    setAudiosCurrent(audiosArray);

    const sentencesArray = audiosArray.map((audio) => audio.sentence);
    setSentences(sentencesArray);
    setIsLoading(false);
  };

  const handlePreviousStep = () => {
    if (step > 1) setStep((prevStep) => prevStep - 1);
    else history.push(route.VOICE_CLONING_VOICES);
  };

  const handleNextStep = () => setStep((prevStep) => prevStep + 1);

  const handleSelectSentence = async () => setStep((prevStep) => prevStep - 1);

  const handleNextFromNotes = async () => {
    await fetchAudios();
    setStep((prevStep) => prevStep + 1);
  };

  const handleBackFromNotes = () => {
    if (hasRecording) {
      history.push(route.VOICE_CLONING_VOICES);
      dispatch(actions.audioVoiceCloning.updateAudios([]));
    } else {
      setStep((prevStep) => prevStep - 1);
    }
  };

  const handleNextFromSendRequest = async () =>
    history.push(route.VOICE_CLONING_VOICES);

  const getDefaultStep = () => {
    const { sentenceCode } = camelcaseKeys(queryString.parse(location.search));
    if (voiceId && sentenceCode) return VOICE_CLONING_STEP.RECORDING;

    if (voiceId) return VOICE_CLONING_STEP.NOTES;

    return VOICE_CLONING_STEP.SELECT_CATEGORY;
  };

  const getLatestEmailUsed = async () => {
    const response = await apis.voiceCloning.getVoiceCloningVoices();
    if (!response?.status) return;

    const voicesArray = response?.result?.voices || [];
    const voicesHaveEmail = voicesArray.filter((voice) => voice.email);
    setLatestEmailUsed(voicesHaveEmail[0]?.email || '');
  };

  const fetchAudiosDataWhenHaveVoice = async () => {
    if (audiosCurrent.length) return;
    if (!voiceId) return;
    await fetchAudios(voiceId);
  };

  useEffect(async () => {
    if (voiceId) {
      await fetchVoice(voiceId);
    }

    setStep(getDefaultStep());
  }, [voiceId]);

  useEffect(() => {
    fetchAudiosDataWhenHaveVoice();
  }, [voiceRecording]);

  useEffect(() => {
    getLatestEmailUsed();
  }, []);

  const renderStepComponent = (currentStep) => {
    switch (currentStep) {
      case VOICE_CLONING_STEP.SELECT_CATEGORY:
        return (
          <CategoryPick
            onNextStep={handleNextStep}
            onPreviousStep={handlePreviousStep}
            hasRecording={voiceId}
          />
        );
      case VOICE_CLONING_STEP.ADD_INFOMATION:
        return (
          <AddInformation
            onNextStep={handleNextStep}
            onPreviousStep={handlePreviousStep}
          />
        );
      case VOICE_CLONING_STEP.NOTES: {
        if (voiceRecording?.status === VOICE_STATUS.REJECTED)
          return (
            <NoteRecordingAgain
              audios={audiosCurrent}
              onPreviousStep={handleBackFromNotes}
              onNextStep={handleNextFromNotes}
            />
          );

        return (
          <Notes
            onNextStep={handleNextFromNotes}
            onPreviousStep={handleBackFromNotes}
          />
        );
      }
      case VOICE_CLONING_STEP.RECORDING:
        return (
          <Recording
            voiceRecording={voiceRecording}
            sentences={sentences}
            audiosCurrent={audiosCurrent}
            onNextStep={handleNextStep}
            onPreviousStep={handlePreviousStep}
          />
        );
      case VOICE_CLONING_STEP.CHECK_INFORMATION:
        return (
          <CheckInformation
            onNextStep={handleNextStep}
            onPreviousStep={handlePreviousStep}
            onSelectSentence={handleSelectSentence}
            voiceRecording={voiceRecording}
            audios={audios}
          />
        );
      case VOICE_CLONING_STEP.SEND_REQUEST:
        return (
          <SendRequest
            voiceRecording={voiceRecording}
            latestEmailUsed={latestEmailUsed}
            onNextStep={handleNextFromSendRequest}
            onPreviousStep={handlePreviousStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          height="90vh"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        !isLoading && renderStepComponent(step)
      )}
    </>
  );
};

export default VoiceCloningCreate;
